import { useEffect, useRef, useState } from 'react';
import {
    GREETING_PHASE,
    GREETING_TIMING_MS,
    REDUCED_MOTION_QUERY,
    TGreetingPhase,
} from 'src/modules/assistant/constants/widget';

interface UseGreetingAnimationParams {
    enabled: boolean;
    messages: string[];
    language: string;
}

/**
 * Cycles through the launcher greetings while the widget is closed:
 * typing dots, then the message written character by character, then a pause.
 */
export const useGreetingAnimation = ({
    enabled,
    messages,
    language,
}: UseGreetingAnimationParams) => {
    const [phase, setPhase] = useState<TGreetingPhase>(GREETING_PHASE.HIDDEN);
    const [messageIndex, setMessageIndex] = useState(0);
    const [visibleCharacters, setVisibleCharacters] = useState(0);
    const messagesRef = useRef(messages);
    messagesRef.current = messages;

    useEffect(() => {
        if (!enabled) {
            setPhase(GREETING_PHASE.HIDDEN);
            return undefined;
        }

        let timer: number;
        let nextMessageIndex = 0;
        const reduceMotion = window.matchMedia?.(REDUCED_MOTION_QUERY).matches;
        const schedule = (callback: () => void, delay: number) => {
            timer = window.setTimeout(callback, delay);
        };
        const finishMessage = () => {
            setPhase(GREETING_PHASE.MESSAGE);
            schedule(() => {
                setPhase(GREETING_PHASE.HIDDEN);
                nextMessageIndex =
                    (nextMessageIndex + 1) % messagesRef.current.length;
                schedule(showMessage, GREETING_TIMING_MS.PAUSE);
            }, GREETING_TIMING_MS.VISIBLE);
        };
        const writeMessage = (message: string, count: number) => {
            setVisibleCharacters(count);
            if (count < message.length)
                schedule(
                    () => writeMessage(message, count + 1),
                    GREETING_TIMING_MS.PER_CHARACTER
                );
            else finishMessage();
        };
        const showMessage = () => {
            const message = messagesRef.current[nextMessageIndex];
            setMessageIndex(nextMessageIndex);
            setVisibleCharacters(0);
            if (reduceMotion) {
                setVisibleCharacters(message.length);
                finishMessage();
                return;
            }
            setPhase(GREETING_PHASE.TYPING);
            schedule(() => {
                setPhase(GREETING_PHASE.WRITING);
                writeMessage(message, 1);
            }, GREETING_TIMING_MS.TYPING);
        };

        schedule(showMessage, GREETING_TIMING_MS.INITIAL_DELAY);
        return () => window.clearTimeout(timer);
    }, [enabled, language]);

    const message = messages[messageIndex];
    return {
        phase,
        message,
        writtenMessage: message.slice(0, visibleCharacters),
    };
};
